import React from "react";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import { HeartPulse, Baby, TreePine, Users2 } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHeader from "../components/SectionHeader";
import { useLanguage } from "../contexts/LanguageContext";
import { IMAGES } from "../data/site";

const pillarIcons = [HeartPulse, Baby, TreePine, Users2];
const pillarImages = [
  "humanitarian",
  "orphan",
  "environment",
  "community",
];

export default function Impact() {
  const { t } = useLanguage();

  return (
    <div data-testid="impact-page">
      <PageHero
        eyebrow={t.impact.eyebrow}
        title={t.impact.title}
        subtitle={t.impact.subtitle}
        image={IMAGES.humanitarian}
      />

      {/* STATS */}
      <section className="py-20 bg-white border-b border-black/5" data-testid="impact-stats">
        <div className="gk-container grid grid-cols-2 lg:grid-cols-4 gap-6">
          {t.impact.stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="text-center"
              data-testid={`stat-${i}`}
            >
              <p className="font-heading font-bold text-5xl sm:text-6xl text-[#0B5D1E] leading-none">
                <CountUp end={s.value} duration={2.2} enableScrollSpy scrollSpyOnce />
                {s.suffix}
              </p>
              <p className="mt-3 text-sm uppercase tracking-[0.15em] text-neutral-600 font-semibold font-heading">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24">
        <div className="gk-container">
          <SectionHeader
            eyebrow={t.impact.eyebrow}
            title={t.impact.title}
            align="center"
          />

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.impact.pillars.map((p, i) => {
              const Icon = pillarIcons[i];
              return (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: i * 0.08, duration: 0.6 }}
                  className="group relative overflow-hidden rounded-3xl bg-white border border-black/5 shadow-sm hover:shadow-xl transition-all"
                  data-testid={`pillar-${i}`}
                >
                  <div className="aspect-square overflow-hidden bg-[#0B5D1E]/5 flex items-center justify-center">
                    <img
                      src={IMAGES[pillarImages[i]]}
                      alt={p.title}
                      className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-2xl bg-[#0B5D1E] text-white flex items-center justify-center flex-shrink-0">
                        <Icon size={22} />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-2xl text-neutral-900">
                          {p.title}
                        </h3>
                        <p className="mt-3 text-neutral-600 leading-relaxed">{p.desc}</p>
                      </div>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
